import { BusinessDocType, FieldDictionaryUsedBy, backendServices, useDocumentSkeleton } from 'common';
import { SIM_DOCUMENT_CLASS } from './_shared/simDocumentVM';
import { SimCustomerIds } from './useEnsureSimCustomers';
import { SupplierPendingSimRequestFlags } from './useSupplierPendingSimRequests';

export interface SimSkeletonFetchContext {
    buyerCompanyId: number;
    supplierCompanyId: number;
    /** TEMPORARY: drop with CSP EnableClientSideCustomerCreation. */
    customerIds: SimCustomerIds;
    /** CSP FeaturesEnabled: EnableClientSideCustomerCreation */
    clientSideCustomerCreationEnabled?: boolean;
}

/** TEMPORARY: while the workaround is on, skeleton fetches wait for the per-class Customer row. */
function customerGate(
    customerId: number | null,
    clientSideCustomerCreationEnabled: boolean
): { customerId?: number; ready: boolean } {
    if (!clientSideCustomerCreationEnabled) {
        return { ready: true };
    }
    return { customerId: customerId ?? undefined, ready: customerId != null };
}

/**
 * The backend captured working GET /Document/Skeleton call used
 * `client=2`, which is FieldDictionaryUsedBy.CSP. The SIM skeletons
 * today do not vary by buyer, so this value can be considered fixed.
 */
const SIM_SKELETON_CLIENT = FieldDictionaryUsedBy.CSP;

type SkeletonData = Awaited<ReturnType<InstanceType<typeof backendServices.Apis.DocumentApi>['getSkeleton']>>['data'];

export interface SimSkeletons {
    taxDetails: SkeletonData | undefined;
    remittance: SkeletonData | undefined;
    certificateOfInsurance: SkeletonData | undefined;
    isLoading: boolean;
    isError: boolean;
}

/**
 * Fetches the SIM skeletons for the three post-signup classes in
 * parallel. Each underlying useDocumentSkeleton call is gated by the
 * corresponding pending-request flag so we never fetch a skeleton we
 * don't need.
 *
 * To hardcode skeletons for manual testing, replace the three
 * `useDocumentSkeleton` calls below with local constants (see the
 * commented example at the bottom of this file).
 */
export function useSimSkeletons(
    flags: SupplierPendingSimRequestFlags,
    context: SimSkeletonFetchContext | null
): SimSkeletons {
    const baseParams = {
        businessDocType: BusinessDocType.SIMDocument,
        client: SIM_SKELETON_CLIENT,
        buyerCompanyId: context?.buyerCompanyId,
        supplierCompanyId: context?.supplierCompanyId,
    };

    const clientSideCustomerCreationEnabled = context?.clientSideCustomerCreationEnabled ?? false;
    const taxGate = customerGate(context?.customerIds.taxDetails ?? null, clientSideCustomerCreationEnabled);
    const remittanceGate = customerGate(context?.customerIds.remittance ?? null, clientSideCustomerCreationEnabled);
    const coiGate = customerGate(
        context?.customerIds.certificateOfInsurance ?? null,
        clientSideCustomerCreationEnabled
    );

    const taxQuery = useDocumentSkeleton({
        ...baseParams,
        customerId: taxGate.customerId,
        documentClass: SIM_DOCUMENT_CLASS.W9,
        enabled: !!context && flags.taxDetails && taxGate.ready,
    });

    const remittanceQuery = useDocumentSkeleton({
        ...baseParams,
        customerId: remittanceGate.customerId,
        documentClass: SIM_DOCUMENT_CLASS.Remittance,
        enabled: !!context && flags.remittance && remittanceGate.ready,
    });

    const coiQuery = useDocumentSkeleton({
        ...baseParams,
        customerId: coiGate.customerId,
        documentClass: SIM_DOCUMENT_CLASS.InsuranceCertificate,
        enabled: !!context && flags.certificateOfInsurance && coiGate.ready,
    });

    const isLoading =
        (flags.taxDetails && taxQuery.isLoading) ||
        (flags.remittance && remittanceQuery.isLoading) ||
        (flags.certificateOfInsurance && coiQuery.isLoading);

    const isError =
        (flags.taxDetails && taxQuery.isError) ||
        (flags.remittance && remittanceQuery.isError) ||
        (flags.certificateOfInsurance && coiQuery.isError);

    return {
        taxDetails: taxQuery.data,
        remittance: remittanceQuery.data,
        certificateOfInsurance: coiQuery.data,
        isLoading,
        isError,
    };
}

/*
 * Manual testing tip: to mock skeletons without hitting the backend,
 * replace the body of useSimSkeletons with something like:
 *
 *   return {
 *       taxDetails: MOCK_SKELETON,
 *       remittance: MOCK_SKELETON,
 *       certificateOfInsurance: MOCK_SKELETON,
 *       isLoading: false,
 *       isError: false,
 *   };
 */
